import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * The label/value rows every detail card in the designs is made of: Customer
 * Details, Payment Info, Reservation Summary, Cost Summary.
 *
 * The value is right-aligned against the label rather than laid out in a fixed
 * two-column grid, so a long value wraps under itself instead of pushing the
 * label off a 320px screen.
 */
export function SummaryRow({
  label,
  children,
  tone,
  className,
}: {
  label: string;
  children: React.ReactNode;
  /** `accent` for the row the card exists to show; `danger` for a deduction. */
  tone?: 'default' | 'accent' | 'danger' | 'muted';
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5', className)}>
      <span className="text-sm text-ink-muted">{label}</span>
      <span
        className={cn(
          'text-sm font-medium tabular-nums',
          tone === 'accent' && 'text-accent',
          tone === 'danger' && 'text-red-600 dark:text-red-400',
          tone === 'muted' && 'font-normal text-ink-muted',
        )}
      >
        {children}
      </span>
    </div>
  );
}

/** A hairline above a summing row — the design draws one over every total. */
export function SummaryRule() {
  return <hr className="my-2 border-line" />;
}

/**
 * The page header the detail frames share: a title, the chips that qualify it,
 * a muted subtitle, and the action cluster.
 *
 * The cluster wraps under the title below `sm` rather than shrinking, which is
 * the responsive behaviour commit `edc85d2` already established for every page
 * header in the app — the 1440-only frames are not permission to drop it.
 */
export function DetailHeader({
  title,
  chips,
  subtitle,
  actions,
  className,
}: {
  title: React.ReactNode;
  chips?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {chips}
        </div>
        {subtitle && <p className="text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
